import { defineSlotRecipe } from '@chakra-ui/react';

export const myAccountMenuRecipe = defineSlotRecipe({
    slots: ['root', 'trigger', 'header', 'body', 'item'],
    base: {
        root: {},
        // The avatar inside the trigger button: the interactive chrome
        // (hover/expanded tints, focus ring) lives on the button itself —
        // this is just the 24px identity chip.
        trigger: {
            boxSize: '24px',
            bg: '{colors.bg.hover}',
            color: '{colors.fg.muted}',
        },
        // The identity cap as the hub onboarding tour's header: always the
        // dark brand surface (both modes) with the field illustration over
        // it at the tour's placement — 90% across, 85% opacity — and a
        // comparable scale.
        header: {
            p: 0,
            position: 'relative',
            isolation: 'isolate',
            bgColor: '#0d1218',
            // The image is app-owned: the component sets this var from its
            // headerBackgroundUrl prop (assets can't live here — the Chakra
            // typegen CLI bundles theme.ts and has no loader for asset
            // imports). On a pseudo-element, for the opacity.
            _before: {
                content: '""',
                position: 'absolute',
                inset: 0,
                zIndex: -1,
                pointerEvents: 'none',
                bgImage: 'var(--account-menu-header-bg, none)',
                bgRepeat: 'no-repeat',
                // The tour's 120% is of a ~600px dialog; on this 256px strip
                // that shrinks the artwork, so it zooms to a similar scale.
                bgSize: '250%',
                bgPos: '90% 50%',
                opacity: 0.85,
            },
        },
        // No bg: the item list sits on the popover panel tone, a step
        // lighter than the identity cap above it.
        body: {
            p: 0,
        },
        item: {
            // Center the icon on the text line — the row Flex would
            // otherwise stretch it and the glyph reads high.
            alignItems: 'center',
            gap: 2,
            px: 2,
            // 32px rows, matching the sidebar's compact nav items; the py
            // only pads labels that wrap past one line.
            py: 1,
            minH: 8,
            fontSize: 'xs',
            fontWeight: 'normal',
            rounded: 'lg',
            transition: 'background 0.2s ease, color 0.2s ease',
            // Full-strength fg like the sidebar items (muted gray is
            // reserved for secondary text, e.g. the header's email line).
            color: '{colors.fg}',
            // Keyboard focus ring, inset so the popover can't clip it.
            // Two shapes render this slot (my-account-menu.tsx): as='button'
            // items focus the element itself (_focusVisible), link items
            // are wrapped by a raw anchor whose UA outline is suppressed
            // inline there — same pattern as the sidebar.
            _focusVisible: {
                outlineWidth: '2px',
                outlineStyle: 'solid',
                outlineColor: 'colorPalette.focusRing',
                outlineOffset: '-2px',
            },
            'a:focus-visible > &': {
                outlineWidth: '2px',
                outlineStyle: 'solid',
                outlineColor: 'colorPalette.focusRing',
                outlineOffset: '-2px',
            },
            _hover: {
                bg: '{colors.bg.hover}',
            },
            '&[data-intent="danger"]': {
                _hover: { bg: '{colors.red.50}', color: '{colors.red.600}' },
                _dark: {
                    _hover: {
                        bg: 'color-mix(in srgb, {colors.red.500} 10%, transparent)',
                        // red.400, like Chakra's own dark error fg: red.600
                        // only reaches 3.04:1 on the composited tint (12px
                        // text needs 4.5); red.400 lands 5.31:1.
                        color: '{colors.red.400}',
                    },
                },
            },
        },
    },
});
